import { DiscoveryApi, IdentityApi } from '@backstage/core-plugin-api';

export type InfraRequestStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'creating'
  | 'created'
  | 'failed';

export interface InfraRequest {
  id: string;
  kind: string;
  status: InfraRequestStatus;
  requester_ref: string;
  inputs: Record<string, any>;
  approver_ref: string | null;
  reason: string | null;
  scaffolder_task_id: string | null;
  decided_at: string | null;
  created_at: string;
  updated_at: string;
}

export class InfraRequestsClient {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly identityApi: IdentityApi,
  ) {}

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('infra-requests');
  }

  private async headers(): Promise<Record<string, string>> {
    const { token } = await this.identityApi.getCredentials();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  async list(opts?: { status?: InfraRequestStatus; kind?: string }): Promise<InfraRequest[]> {
    const base = await this.baseUrl();
    const params = new URLSearchParams();
    if (opts?.status) params.set('status', opts.status);
    if (opts?.kind) params.set('kind', opts.kind);
    const qs = params.toString();
    const url = qs ? `${base}/requests?${qs}` : `${base}/requests`;
    const resp = await fetch(url, { headers: await this.headers() });
    if (!resp.ok) throw new Error(`Failed to list requests: ${resp.statusText}`);
    return resp.json();
  }

  async get(id: string): Promise<InfraRequest> {
    const base = await this.baseUrl();
    const resp = await fetch(`${base}/requests/${id}`, { headers: await this.headers() });
    if (!resp.ok) throw new Error(`Failed to get request: ${resp.statusText}`);
    return resp.json();
  }

  async decide(
    id: string,
    decision: 'approve' | 'reject',
    opts?: { taskId?: string; reason?: string },
  ): Promise<void> {
    const base = await this.baseUrl();
    const resp = await fetch(`${base}/requests/${id}/decision`, {
      method: 'POST',
      headers: await this.headers(),
      body: JSON.stringify({ decision, ...opts }),
    });
    if (!resp.ok) {
      const body = await resp.text();
      throw new Error(`Failed to ${decision} request: ${body}`);
    }
  }

  async updateStatus(id: string, status: string): Promise<void> {
    const base = await this.baseUrl();
    const resp = await fetch(`${base}/requests/${id}/status`, {
      method: 'POST',
      headers: await this.headers(),
      body: JSON.stringify({ status }),
    });
    if (!resp.ok) {
      const body = await resp.text();
      throw new Error(`Failed to update status: ${body}`);
    }
  }
}
